import Button from "@/components/Button";
import Input from "@/components/Input";
import InputPhoneNumber from "@/components/InputPhoneNumber";
import ID_ICON from "@/assets/icons/ID.svg";
import { ReactComponent as AVATAR_ICON } from "@/assets/icons/avatar.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ReactComponent as HelpIcon } from "@/assets/icons/help-circle.svg";
import Dropdown from "@/components/Dropdown";
import InputPass from "@/components/InputPassword";
import SelectImage from "@/components/SelectImage";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  ROLE_OPTIONS,
  ERROR_FIELD,
  GENDER_OPTIONS,
  VALID_IMAGE_TYPE,
} from "./constant";
import { OptionItem } from "@/components/Dropdown/model";
import { isHaveWhiteSpace, isNotValidEmail } from "@/utils/form";
import DatePick from "@/components/DatePicker";
import TextArea from "@/components/TextArea";
import { useLocation, useNavigate } from "react-router-dom";
import "./style.scss";
import getUserByIDInteractor from "@/@core/interactor/user/get-user-by-id.interactor";
import createUserInteractor, {
  CreateUserDTO,
} from "@/@core/interactor/user/create-user.interactor";
import { stringToDate } from "@/utils/date";
import editUserInteractor from "@/@core/interactor/user/edit-user.interactor";
import toast, { Toaster } from "react-hot-toast";
import uploadFileInteractor from "@/@core/interactor/media/upload-file";
import { validateImageResolution } from "@/utils/image";
import getWarehouseInteractor from "@/@core/interactor/warehouse/get-warehouse.interactor";
import { Warehouse } from "@/@core/entity/warehouse.entity";

const UserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { procedure, userId } = state;

  const [isTouchSubmit, setIsTouchSubmit] = useState<boolean>(false);
  const [initialPage, setInitialPage] = useState(false);
  useEffect(() => setInitialPage(true), []);

  const [photoFile, setPhotoFile] = useState<File>();
  const [photoProfile, setPhotoProfile] = useState<any>(null);
  const [isValidResolution, setIsValidResolution] = useState<boolean>(true);
  const isErrorPhotoProfile = useMemo(() => {
    if (photoFile) {
      return (
        VALID_IMAGE_TYPE.indexOf(photoFile.type) === -1 &&
        (initialPage || isTouchSubmit)
      );
    } else {
      return false;
    }
  }, [photoFile]);

  const [fullName, setFullName] = useState<string>("");
  const isErrorFullname = useMemo(() => {
    // return fullName.length < 8 && (initialPage || isTouchSubmit);
    return fullName.length === 0 && (initialPage || isTouchSubmit);
  }, [fullName, isTouchSubmit]);

  const [username, setUsername] = useState<string>("");
  const isErrorUsername = useMemo(() => {
    return (
      (username.length === 0 || isHaveWhiteSpace(username)) &&
      (initialPage || isTouchSubmit)
    );
  }, [username, isTouchSubmit]);

  const [emailAddress, setEmailAddress] = useState<string>("");
  const isErrorEmailAddress = useMemo(() => {
    return (
      (emailAddress.length === 0 || isNotValidEmail(emailAddress)) &&
      (initialPage || isTouchSubmit)
    );
  }, [emailAddress, isTouchSubmit]);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const isErrorPhoneNumber = useMemo(() => {
    return phoneNumber.length < 10 && (initialPage || isTouchSubmit);
  }, [phoneNumber, isTouchSubmit]);

  const [dob, setDob] = useState<Date | null>(null);
  const isErrorDob = useMemo(() => {
    return dob === null && (initialPage || isTouchSubmit);
  }, [dob, isTouchSubmit]);

  const [gender, setGender] = useState<OptionItem>(GENDER_OPTIONS[0]);
  const [bio, setBio] = useState<string>("");
  const [role, setRole] = useState<OptionItem>(ROLE_OPTIONS[0]);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isFetch, setIsFetch] = useState(false);
  const [warehouse, setWarehouse] = useState<any>();
  const [listWarehouse, setListWarehouse] = useState<Warehouse[]>([]);

  const isErrorPassword = useMemo(() => {
    if (procedure === "edit") {
      return password !== confirmPassword && (initialPage || isTouchSubmit);
    } else {
      return (
        (password.length === 0 || password !== confirmPassword) &&
        (initialPage || isTouchSubmit)
      );
    }
  }, [password, confirmPassword, isTouchSubmit]);

  const isErrorConfirmPassword = useMemo(() => {
    if (procedure === "edit") {
      return password !== confirmPassword && (initialPage || isTouchSubmit);
    } else {
      return (
        (confirmPassword.length === 0 || password !== confirmPassword) &&
        (initialPage || isTouchSubmit)
      );
    }
  }, [confirmPassword, password, isTouchSubmit]);

  const getWarehouse = async () => {
    try {
      const response = await getWarehouseInteractor({});
      const {
        data: { data },
      } = response;
      const newData = data.filter((warehouse) => warehouse.is_active === true);
      setListWarehouse(newData);
    } catch (error: any) {
      setListWarehouse([]);
    }
  };

  useEffect(() => {
    getWarehouse();
    switch (procedure) {
      case "edit":
        getUserByID();
        break;
    }
  }, []);

  const getUserByID = async () => {
    try {
      const response = await getUserByIDInteractor({ userId });

      const { data } = response;
      const userDetail = data.data;

      setFullName(userDetail.fullname);
      setUsername(userDetail.username);
      setEmailAddress(userDetail.email);
      setPhoneNumber(userDetail.phone_number);
      setDob(new Date(userDetail.date_of_birth));
      setBio(userDetail.bio);

      const selectedGender = GENDER_OPTIONS.find(
        (genderItem) => genderItem.value === userDetail.gender
      );
      if (selectedGender) setGender(selectedGender);

      const selectedRole = ROLE_OPTIONS.find(
        (roleItem) => roleItem.value === userDetail.role
      );
      if (selectedRole) setRole(selectedRole);

      if (userDetail.photo_profile) {
        setPhotoProfile(
          `${process.env.REACT_APP_API_URL}/file/${userDetail.photo_profile}`
        );
      }
    } catch (error: any) {
      const { response } = error;
      const {
        data: { message },
      } = response;

      toast.error(message);
    }
  };

  const onChangePhotoProfile = async (e: ChangeEvent<HTMLInputElement>) => {
    const files: any = e.target.files;

    if (files && files.length) {
      const isValidRes = await validateImageResolution(files[0], 2048, 2048);
      setIsValidResolution(isValidRes);
      setPhotoFile(files[0]);

      if (VALID_IMAGE_TYPE.indexOf(files[0].type) !== -1 && isValidRes) {
        const objectUrl = URL.createObjectURL(files[0]);
        setPhotoProfile(objectUrl);
      } else {
        setPhotoProfile(null);
      }
    }
  };

  const onSubmit = async () => {
    setIsTouchSubmit(true);

    if (
      isErrorUsername === false &&
      isErrorEmailAddress === false &&
      isErrorPhoneNumber === false &&
      isErrorDob === false &&
      isErrorPassword === false &&
      isErrorConfirmPassword === false &&
      isErrorPhotoProfile === false &&
      isValidResolution
    ) {
      setIsFetch(true);
      try {
        if (photoFile) {
          const response: any = await uploadFileInteractor(photoFile);
          const { status, message } = response;
          if (status === 201 || status === 200) {
            const uploadImageId = response?.data?.data?.id;
            procedure === "add"
              ? createUser(uploadImageId)
              : updateUser(uploadImageId);
          } else {
            toast.error(message);
          }
        } else {
          procedure === "add" ? createUser(-1) : updateUser(-1);
        }
      } catch (error: any) {
        handleError(error);
      } finally {
        setIsFetch(false);
      }
    }
  };

  const getBody = (media_id: number = -1) => {
    const date_of_birth: any = dob;
    const body: CreateUserDTO = {
      fullname: fullName,
      username,
      email: emailAddress,
      phone_number: phoneNumber,
      date_of_birth: stringToDate(date_of_birth, "dd/mm/yyyy"),
      gender: gender.value,
      bio,
      role: role.value,
      warehouse_id: warehouse.value,
      password,
      confirm_password: confirmPassword,
    };

    if (media_id !== -1) {
      body.media_id = media_id;
    }

    return body;
  };

  const createUser = async (uploadImageId: number) => {
    try {
      const body = getBody(uploadImageId);
      const response = await createUserInteractor(body);

      const { status } = response;

      if (status === 200) {
        navigate("/users");
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const updateUser = async (uploadImageId: number) => {
    try {
      const body: any = getBody(uploadImageId);

      if (!body.password || !body.confirm_password) {
        delete body.password;
        delete body.confirm_password;
      }

      const response = await editUserInteractor({
        pathParam: { userId },
        body,
      });

      const { status } = response;
      if (status === 200) {
        navigate("/users");
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleError = (error: any) => {
    const { response } = error;
    const {
      data: { message },
    } = response;

    toast.error(message);
  };

  return (
    <div className='user-form-page-wrapper'>
      <div className='page-header'>
        <h1 onClick={() => toast.error("asd")}>
          {procedure === "add" ? "Add" : "Edit"} User
        </h1>

        <div className='btn-wrapper'>
          <Button
            onClick={() => navigate(-1)}
            label='Cancel'
            type='secondary'
            size='md'
          />
          <Button
            onClick={onSubmit}
            disabled={isFetch}
            label={isFetch ? "Loading" : "Save"}
            startIcon='save'
            size='md'
          />
        </div>
      </div>

      <div className='page-content'>
        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Full name</label>
          </div>
          <div className='right-section'>
            <Input
              style={{ width: "512px" }}
              value={fullName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setFullName(e.target.value);
              }}
              placeholder='User full name'
              isError={isErrorFullname}
              errorText={ERROR_FIELD.fullname}
              disabled={isFetch}
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Username</label>
          </div>
          <div className='right-section'>
            <Input
              style={{ width: "512px" }}
              value={username}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUsername(e.target.value)
              }
              placeholder='User username'
              isError={isErrorUsername}
              errorText={ERROR_FIELD.username}
              disabled={isFetch}
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Email address</label>
          </div>
          <div className='right-section'>
            <Input
              withIcon
              type='email'
              value={emailAddress}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setEmailAddress(e.target.value)
              }
              placeholder='User email address'
              isError={isErrorEmailAddress}
              errorText={ERROR_FIELD.email}
              disabled={isFetch}
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Phone number</label>
          </div>
          <div className='right-section'>
            <InputPhoneNumber
              value={{
                imgSrc: ID_ICON,
                countryLabel: "Indonesia",
                countryCode: "ID",
                countryPhoneCode: "+62",
              }}
              inputValue={phoneNumber}
              options={[
                {
                  imgSrc: ID_ICON,
                  countryLabel: "Indonesia",
                  countryCode: "ID",
                  countryPhoneCode: "+62",
                },
              ]}
              onChange={(e) => {}}
              onChangeInputValue={(value: string) => setPhoneNumber(value)}
              disabled={isFetch}
              width='482px'
              placeholder='User phone number'
              isError={isErrorPhoneNumber}
              errorText={ERROR_FIELD.phonNumber}
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Date of birth</label>
          </div>
          <div className='right-section'>
            <DatePick
              value={dob}
              onChange={(e) => {
                setDob(e);
              }}
              style={{ width: "510px" }}
              isError={isErrorDob}
              errorText={ERROR_FIELD.dob}
              disabled={isFetch}
              showMonthDropdown
              showYearDropdown
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Gender</label>
          </div>
          <div className='right-section'>
            <Dropdown
              placeHolder='Select Option...'
              value={gender}
              options={GENDER_OPTIONS}
              onChange={(e) => setGender(e)}
              optionType='text-option'
              width='512px'
              disabled={isFetch}
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div
            className='left-section'
            style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label>Profile photo</label>
            <HelpIcon
              data-tooltip-content='test'
              className='ask-profile-photo-icon'
            />
            <ReactTooltip
              anchorSelect='.ask-profile-photo-icon'
              place='bottom'
            />
          </div>
          <div
            className='right-section'
            style={{ display: "flex", gap: "20px" }}>
            {photoProfile ? (
              <img
                className='selected-photo-profile'
                src={photoProfile}
                alt='profile'
              />
            ) : (
              <AVATAR_ICON />
            )}
            <SelectImage
              width='430px'
              onChange={onChangePhotoProfile}
              disabled={isFetch}
              displayIcon={true}
              desc='SVG, PNG, JPG or GIF (max. 2048x2048px)'
              isError={isErrorPhotoProfile || !isValidResolution}
              errorText='Image type must be one of svg, png, jpg/jpeg, gif, and have maximun 2048 x 2048 resolution'
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Bio</label>
            <span className='desc'>Write a short introduction.</span>
          </div>
          <div className='right-section'>
            <TextArea
              maxLength={160}
              value={bio}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBio(e.target.value)
              }
              placeholder='User bio'
              disabled={isFetch}
            />
          </div>
        </div>

        <div className='field-wrapper'>
          <div className='left-section'>
            <label>Role</label>
            <span className='desc'>Manage your team members here.</span>
          </div>
          <div className='right-section'>
            <Dropdown
              placeHolder='Select Option...'
              value={role}
              options={ROLE_OPTIONS}
              onChange={(e) => {
                setRole(e)
                setWarehouse('')
              }}
              optionType='text-option'
              width='512px'
              disabled={isFetch}
            />
          </div>
        </div>

        {role.value === "admin" && (
          <div className='field-wrapper'>
            <div className='left-section'>
              <label>Warehouse</label>
              <span className='desc'>Select warehouse to manage</span>
            </div>
            <div className='right-section'>
              <Dropdown
                placeHolder='Select warehouse'
                value={warehouse}
                options={listWarehouse?.map((warehouse: Warehouse) => {
                  const option = {
                    label: warehouse.name,
                    value: warehouse.id,
                  };
                  return option;
                })}
                onChange={(e) => {
                  setWarehouse(e);
                }}
                optionType='text-option'
                width='512px'
                disabled={isFetch}
              />
            </div>
          </div>
        )}

        <div
          className='field-wrapper'
          style={{ paddingBottom: "0px", borderBottom: "unset" }}>
          <div className='left-section'>
            <label>Password</label>
          </div>
          <div className='right-section'>
            <InputPass
              style={{ width: "512px" }}
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              isError={isErrorPassword}
              errorText={ERROR_FIELD.password}
              disabled={isFetch}
            />
          </div>
        </div>

        <div className='field-wrapper' style={{ borderBottom: "unset" }}>
          <div className='left-section'>
            <label>Confirm Password</label>
          </div>
          <div className='right-section'>
            <InputPass
              style={{ width: "512px" }}
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setConfirmPassword(e.target.value)
              }
              isError={isErrorConfirmPassword}
              errorText={ERROR_FIELD.confirmPassword}
              disabled={isFetch}
            />
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
};

export default UserForm;
